import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { ErrorState } from "@common/index";
import { useVendorApi } from "@/hooks/apiHooks/useVendorApi";
import { useMountRead } from "@/hooks/useMountRead";
import { asApiFailure } from "@utils/apiFailure";
import { NotFoundPage } from "@customer/ErrorPages/NotFoundPage";
import { ProductGrid } from "@customer/Browse/components/ProductGrid";
import { SearchResults } from "@customer/Search/SearchResults";
import { isVendorSlug } from "@customer/_helpers/vendorSlug";
import type { VendorPageResponse } from "@models/vendor";

// /c/v/:vendorSlug (#425, DecisionLog §4.14 2026-10-23): the vendor's name and
// the search grid (sort, filters, paging) with the vendor fixed. An unknown or
// inactive vendor is not found. No breadcrumb (Q5): the heading is the name.
//
// The route boundary, as BrowseSubcategory's. A slug the API's own rule
// refuses names no vendor, so it gets the not-found page any unknown URL gets
// and no read is sent. The API would answer it with a 400 rather than a 404,
// but it means the same thing to the shopper, a link to no vendor; sending it
// would only add a validation toast over the same page.
export const VendorPage = () => {
    const { vendorSlug = "" } = useParams<{ vendorSlug: string }>();

    if (!isVendorSlug(vendorSlug)) {
        return <NotFoundPage />;
    }

    return <VendorPageContent slug={vendorSlug} />;
};

interface VendorPageContentProps {
    slug: string;
}

const VendorPageContent = ({ slug }: VendorPageContentProps) => {
    const { getVendorBySlug } = useVendorApi();

    // What the latest read answered: the vendor, or whether its failure was a
    // 404. Each is read only together with that read's status (below), and
    // only the latest read writes them (`isCurrent`), so a slug change needs
    // no reset: the status is "loading" on the render where the slug changes,
    // and an answer for a slug the page has left is never written.
    const [loadedVendor, setLoadedVendor] =
        useState<VendorPageResponse | null>(null);
    const [isNotFound, setIsNotFound] = useState(false);

    const readVendor = useCallback(
        async (isCurrent: () => boolean) => {
            try {
                const data = await getVendorBySlug(slug);
                if (isCurrent()) setLoadedVendor(data);
            } catch (error: unknown) {
                // Duck-typed, as ProductDetail does: component tests mock the
                // useApi module away, so `instanceof ApiError` cannot work.
                // Set on both branches, so a failure after a 404 for the same
                // slug reaches the retryable failed state.
                if (isCurrent()) {
                    setIsNotFound(asApiFailure(error)?.status === 404);
                }
                throw error;
            }
        },
        [slug, getVendorBySlug],
    );

    // The status of the read THIS mount issued for THIS slug (#407).
    const { readStatus, reload } = useMountRead(readVendor);

    // A 404 is also a failed read, so it is answered first, or it would render
    // as a generic failure. Every other failure (a 5xx, a 403, a dropped
    // network) is a failed read with Retry, never "not found" and never an
    // empty grid.
    if (readStatus === "failed") {
        if (isNotFound) {
            return <NotFoundPage />;
        }
        return (
            <div className="max-w-7xl mx-auto px-4 py-6">
                <ErrorState onRetry={reload} />
            </div>
        );
    }

    const vendor = readStatus === "loaded" ? loadedVendor : null;

    if (!vendor) {
        return (
            <div
                role="status"
                aria-label="Loading vendor"
                className="max-w-7xl mx-auto px-2 md:px-4"
            >
                <ProductGrid
                    products={[]}
                    pageNumber={1}
                    totalPages={0}
                    isLoading
                />
            </div>
        );
    }

    // No key is needed for a move to another vendor's page: the slug change
    // makes the status "loading" on that very render, so the skeleton above
    // replaces this grid, and the next vendor's grid mounts fresh.
    return <SearchResults fixedVendor={vendor} />;
};
